import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import StraightenIcon from '@mui/icons-material/Straighten';
import RouteIcon from '@mui/icons-material/Route';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import LoopIcon from '@mui/icons-material/Loop';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';
import { i18n } from 'i18n';
import { Model } from '../../../hooks/useModelState';
import SpoolReportDialog from '../../../SpoolReport/SpoolReportDialog';
import ToolbarSection from './ToolbarSection';
import ToolbarButton from './ToolbarButton';
import ToolbarTooltip from './ToolbarTooltip';
import SplitToolbarButton from './SplitToolbarButton';
import PipeLengthTotalsPopout from './PipeLengthTotalsPopout';
import FlowLoopLengthsPopout from './FlowLoopLengthsPopout';
import RouteVolumePopout from './RouteVolumePopout';
import {
  getMeasureState,
  subscribeToMeasureState,
  updateMeasureState,
  deactivateMeasure,
  emptyMeasureResults,
  MeasureMode
} from './measureState';

interface UtilitiesTabProps {
  model: Model;
}

const MEASURE_MODE_LABELS: Record<MeasureMode, string> = {
  'node-to-node': 'Node → Node',
  'spool-click': 'Spool',
  route: 'Route',
  'route-volume': 'Route Volume',
};

const MEASURE_MODE_DESCRIPTIONS: Record<MeasureMode, string> = {
  'node-to-node': 'Measure straight-line distance between two nodes (click two nodes).',
  'spool-click': 'Click a spool to measure its length.',
  route: 'Click two nodes to measure the shortest route between them along pipes.',
  'route-volume': 'Click two nodes to measure the internal volume of the shortest route, using each pipe size along the way.',
};

const MEASURE_MODE_ICONS: Record<MeasureMode, React.ReactNode> = {
  'node-to-node': <StraightenIcon fontSize="small" />,
  'spool-click': <LinearScaleIcon fontSize="small" />,
  route: <RouteIcon fontSize="small" />,
  'route-volume': <WaterDropIcon fontSize="small" />,
};

const MEASURE_MODE_KEYS: MeasureMode[] = ['node-to-node', 'spool-click', 'route', 'route-volume'];

const ROUTE_MODES: MeasureMode[] = ['route', 'route-volume'];

const UtilitiesTab = ({ model }: UtilitiesTabProps) => {
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);
  const [popoutOpen, setPopoutOpen] = React.useState(false);
  const [flowLoopPopoutOpen, setFlowLoopPopoutOpen] = React.useState(false);
  const [routeVolumePopoutOpen, setRouteVolumePopoutOpen] = React.useState(false);
  const [spoolReportOpen, setSpoolReportOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const flowLoopAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const routeVolumeAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const {
    measureMode,
    measureActive,
    routeNoPathFound,
    routeVolumeResult,
  } = getMeasureState();

  const handleToggleMeasureActive = () => {
    if (measureActive) {
      deactivateMeasure();
      return;
    }
    updateMeasureState({ measureActive: true });
  };

  const handleSetMode = (mode: MeasureMode) => {
    updateMeasureState({ ...emptyMeasureResults(), measureMode: mode, measureActive: true });
  };

  const handleClear = () => {
    updateMeasureState({ ...emptyMeasureResults() });
  };

  const measureMenuItems = MEASURE_MODE_KEYS.map((key) => ({
    key,
    label: i18n._(MEASURE_MODE_LABELS[key]),
    description: i18n._(MEASURE_MODE_DESCRIPTIONS[key]),
    tooltipTitle: i18n._(MEASURE_MODE_LABELS[key]),
  }));

  React.useEffect(() => subscribeToMeasureState(() => forceUpdate()), []);

  return (
    <>
      <ToolbarSection label={i18n._('Measure')}>
        <SplitToolbarButton
          label={i18n._(MEASURE_MODE_LABELS[measureMode])}
          tooltipTitle={measureActive ? i18n._('Turn measure off') : i18n._('Turn measure on')}
          description={i18n._('Click to toggle the measure tool. Use the dropdown to pick a mode. Press Escape to stop measuring.')}
          icon={MEASURE_MODE_ICONS[measureMode]}
          onClick={handleToggleMeasureActive}
          onMenuItemClick={(key) => handleSetMode(key as MeasureMode)}
          menuItems={measureMenuItems}
          selectedKey={measureMode}
          selected={measureActive}
        />
        {measureActive && (
          <ToolbarTooltip title={i18n._('Clear measurement')} description={i18n._('Reset selected nodes and measurement results.')}>
            <Button
              size="small"
              color="secondary"
              onClick={handleClear}
              sx={{ textTransform: 'none', fontSize: 10, py: 0, minWidth: 0 }}
            >
              {i18n._('Clear')}
            </Button>
          </ToolbarTooltip>
        )}
        {measureActive && ROUTE_MODES.includes(measureMode) && routeNoPathFound && (
          <Typography variant="caption" sx={{ fontSize: 10, color: 'error.main', whiteSpace: 'nowrap' }}>
            {i18n._('No path found')}
          </Typography>
        )}
        {measureMode === 'route-volume' && routeVolumeResult && (
          <Box ref={routeVolumeAnchorRef}>
            <ToolbarButton
              label={i18n._('Breakdown')}
              tooltipTitle={i18n._('Route volume breakdown')}
              description={i18n._('Show the measured route split by pipe type, with the length and internal volume of each.')}
              icon={<FormatListBulletedIcon fontSize="small" />}
              onClick={() => setRouteVolumePopoutOpen(true)}
              selected={routeVolumePopoutOpen}
            />
          </Box>
        )}
        <RouteVolumePopout
          open={routeVolumePopoutOpen && !!routeVolumeResult}
          anchorEl={routeVolumeAnchorRef.current}
          onClose={() => setRouteVolumePopoutOpen(false)}
          model={model}
          routeVolume={routeVolumeResult}
        />
      </ToolbarSection>
      <Divider orientation="vertical" flexItem />
      <ToolbarSection label={i18n._('Lengths')}>
        <Box ref={anchorRef}>
          <ToolbarButton
            label={i18n._('Pipe Totals')}
            tooltipTitle={i18n._('Pipe length totals')}
            description={i18n._('Show total length per pipe type across the whole model.')}
            icon={<FormatListBulletedIcon fontSize="small" />}
            onClick={() => setPopoutOpen(true)}
            selected={popoutOpen}
          />
        </Box>
        <Box ref={flowLoopAnchorRef}>
          <ToolbarButton
            label={i18n._('Flow Loops')}
            tooltipTitle={i18n._('Flow loop lengths')}
            description={i18n._('Show each flow loop with its group, node name, loop length, and pipe type.')}
            icon={<LoopIcon fontSize="small" />}
            onClick={() => setFlowLoopPopoutOpen(true)}
            selected={flowLoopPopoutOpen}
          />
        </Box>
        <ToolbarButton
          label={i18n._('Spool Report')}
          tooltipTitle={i18n._('Spool Report')}
          description={i18n._('Group spools by date, pipe type, level, area or status, total their lengths, and copy or export the table.')}
          icon={<PivotTableChartIcon fontSize="small" />}
          onClick={() => setSpoolReportOpen(true)}
          selected={spoolReportOpen}
        />
        <SpoolReportDialog open={spoolReportOpen} onClose={() => setSpoolReportOpen(false)} model={model} />
        <PipeLengthTotalsPopout
          open={popoutOpen}
          anchorEl={anchorRef.current}
          onClose={() => setPopoutOpen(false)}
          model={model}
        />
        <FlowLoopLengthsPopout
          open={flowLoopPopoutOpen}
          anchorEl={flowLoopAnchorRef.current}
          onClose={() => setFlowLoopPopoutOpen(false)}
          model={model}
        />
      </ToolbarSection>
    </>
  );
};

export default UtilitiesTab;
